import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type { IMetaPage, OmniCampaign } from '@feathr/blackbox';
import { CardV2 as Card, UniversalSelect } from '@feathr/components';
import { TooltipOption } from '@feathr/extender/components/SelectOptions';

import MetaAdCategorySelect from './MetaAdCategorySelect';
import type { IPlatformOption } from './MetaChannelSettingsCard.hooks';
import { useMetaChannelSettingsCard } from './MetaChannelSettingsCard.hooks';

interface IMetaChannelSettingsCardProps {
  campaign: OmniCampaign;
}

function MetaChannelSettingsCard({
  campaign,
}: Readonly<IMetaChannelSettingsCardProps>): JSX.Element {
  const { t } = useTranslation();
  const {
    facebookPages,
    isLoadingFacebookPages,
    selectedFacebookPage,
    handleFacebookPageSelect,
    hasSingleAccount,
    basePlatformOptions,
    hasInstagramPermissions,
    selectedPlatform,
    handlePublisherPlatformsSelect,
  } = useMetaChannelSettingsCard(campaign);

  const disabledTooltip = t(
    'Instagram platforms cannot be selected because Feathr does not have appropriate permissions to advertise on Instagram. Manage your Meta integration to grant permissions.',
  );

  const platformOptions: IPlatformOption[] = hasInstagramPermissions
    ? basePlatformOptions
    : [
        basePlatformOptions[0],
        { ...basePlatformOptions[1], isDisabled: true, tooltip: disabledTooltip },
        { ...basePlatformOptions[2], isDisabled: true, tooltip: disabledTooltip },
      ];

  function getOptionLabel(page: IMetaPage): string {
    return page.name;
  }

  function getOptionValue(page: IMetaPage): string {
    return page.id;
  }

  return (
    <Card>
      <Card.Header
        description={t('Select which Facebook page this campaign will be served from.')}
        title={t('Facebook Page')}
      />
      <Card.Content addVerticalGap={true}>
        <UniversalSelect<IMetaPage>
          getOptionLabel={getOptionLabel}
          getOptionValue={getOptionValue}
          helpText={t('Select the page and account that will be used for this campaign.')}
          isLoading={isLoadingFacebookPages}
          label={hasSingleAccount ? t('Facebook page') : t('Facebook page & Instagram account')}
          name={'facebook-page-select'}
          onSelectSingle={handleFacebookPageSelect}
          options={facebookPages}
          placeholder={t('Select a page/account')}
          required={true}
          value={selectedFacebookPage}
        />

        <UniversalSelect<IPlatformOption>
          components={{ Option: TooltipOption }}
          getOptionLabel={(option: IPlatformOption): string => option.name}
          getOptionValue={(option: IPlatformOption): string => option.id}
          helpText={
            <Trans t={t}>
              Select platforms where ads will be displayed.{' '}
              <a
                href={
                  'https://help.feathr.co/hc/en-us/articles/360048057553-How-to-launch-a-Facebook-campaign-from-Feathr'
                }
                target={'_blank'}
              >
                Learn more about publisher platforms
              </a>
            </Trans>
          }
          label={t('Publisher Platforms')}
          name={'publisher_platforms_select'}
          onSelectSingle={handlePublisherPlatformsSelect}
          options={platformOptions}
          placeholder={t('Select a platform')}
          required={true}
          value={selectedPlatform}
        />

        <MetaAdCategorySelect campaign={campaign} />
      </Card.Content>
    </Card>
  );
}

export default observer(MetaChannelSettingsCard);
